import { useEffect, useState } from 'react';
import type { AuditEvent } from '@reef/schema';
import type { StepRunSummary } from '../api.ts';
import { IdentityBadge, identityKindFor } from './IdentityBadge.tsx';
import { request } from '../http.ts';

/**
 * A run as it happens.
 *
 * The ordering decision is the whole component: this is not a progress bar. A
 * progress bar would say a run is 60% done, which is the least interesting true
 * thing about it. What reef knows and other tools do not is what a step was
 * *refused* — the query truncated to the policy ceiling, the column dropped on
 * the way back, the write parked in Approvals instead of performed, the step
 * moved into a container because the isolate could not run it. So those events
 * are the ones that get the width of the card and a headline, and the routine
 * lifecycle — started, finished, another tool call — is deliberately quiet.
 * Rendering every row at the same weight would bury the argument the product is
 * making about itself under the noise of it working normally.
 *
 * Every row here comes from `reef_audit`, so what is on screen while the run is
 * going is the same record that will be there in a year.
 */
export function RunTimeline({ runId }: { runId: string }) {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [steps, setSteps] = useState<TimelineStep[]>([]);
  const [status, setStatus] = useState<string>('pending');
  const [transport, setTransport] = useState<Transport>('connecting');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;
    let source: EventSource | null = null;
    let poll: ReturnType<typeof setInterval> | undefined;
    let watchdog: ReturnType<typeof setTimeout> | undefined;

    // The cursor lives in the closure rather than in state because the poller
    // and the stream handlers both advance it between renders, and a stale
    // capture would re-request the whole run every tick.
    let cursor = '';
    let arrived = false;

    const apply = (snapshot: TimelinePayload) => {
      if (disposed) return;
      if (snapshot.cursor) cursor = snapshot.cursor;
      const incoming = snapshot.events;
      if (incoming) setEvents((previous) => merge(previous, incoming));
      if (snapshot.steps) setSteps(snapshot.steps);
      if (snapshot.run) setStatus(snapshot.run.status);
      setError(null);
    };

    /**
     * Polling is not a theoretical fallback. `EventSource` reports a failed
     * connection and a closed one identically, and the failures that matter
     * here are silent: a corporate proxy that buffers `text/event-stream` until
     * the response completes, a gateway that strips the content type, a browser
     * already at its six-connection-per-origin cap. In every one of those the
     * stream opens and then simply never says anything, so the watchdog below
     * treats "no snapshot yet" as the failure signal rather than waiting for an
     * error that will not come.
     */
    const fallBack = () => {
      if (disposed || poll) return;
      source?.close();
      source = null;
      setTransport('polling');

      const tick = async () => {
        try {
          const query = cursor ? `?since=${encodeURIComponent(cursor)}` : '';
          const snapshot = await request<TimelinePayload>(
            `/runs/${encodeURIComponent(runId)}/timeline${query}`,
          );
          apply(snapshot);
          if (snapshot.run && TERMINAL.has(snapshot.run.status)) stopPolling();
        } catch (caught) {
          if (!disposed) setError((caught as Error).message);
        }
      };

      const stopPolling = () => {
        if (poll) clearInterval(poll);
        poll = undefined;
        if (!disposed) setTransport('closed');
      };

      void tick();
      poll = setInterval(() => void tick(), POLL_INTERVAL_MS);
    };

    source = new EventSource(`/api/v1/runs/${encodeURIComponent(runId)}/stream`);

    // Same-origin, so the session cookie rides along without `withCredentials`
    // — which cross-origin would also require a CORS allowlist reef does not
    // have a reason to open.
    source.addEventListener('snapshot', (message) => {
      arrived = true;
      if (watchdog) clearTimeout(watchdog);
      setTransport('live');
      apply(JSON.parse((message as MessageEvent<string>).data) as TimelinePayload);
    });

    source.addEventListener('append', (message) => {
      apply(JSON.parse((message as MessageEvent<string>).data) as TimelinePayload);
    });

    source.addEventListener('steps', (message) => {
      const payload = JSON.parse((message as MessageEvent<string>).data) as TimelinePayload;
      if (payload.steps) setSteps(payload.steps);
    });

    source.addEventListener('status', (message) => {
      const payload = JSON.parse((message as MessageEvent<string>).data) as { status: string };
      setStatus(payload.status);
    });

    source.addEventListener('done', (message) => {
      const payload = JSON.parse((message as MessageEvent<string>).data) as {
        status: string;
        reason: string;
      };
      setStatus(payload.status);
      // `expired` means the server hit its own wall-clock bound on a run that
      // is still going, and EventSource's own reconnect is exactly the right
      // response — so it is the one reason we do not close on.
      if (payload.reason === 'expired') return;
      if (payload.reason === 'error') {
        fallBack();
        return;
      }
      source?.close();
      setTransport('closed');
    });

    source.onerror = () => {
      // A stream that has already delivered a snapshot and then errors is the
      // normal end of a bounded connection; letting EventSource retry replays
      // a fresh snapshot, which merges by id and changes nothing on screen.
      if (!arrived) fallBack();
    };

    watchdog = setTimeout(() => {
      if (!arrived) fallBack();
    }, STREAM_WATCHDOG_MS);

    return () => {
      disposed = true;
      source?.close();
      if (poll) clearInterval(poll);
      if (watchdog) clearTimeout(watchdog);
    };
  }, [runId]);

  const spendCents = steps.reduce((total, step) => total + step.spendCents, 0);

  return (
    <div className="stack">
      <div className="row wrap">
        <p className="section-title" style={{ margin: 0, flex: 1, minWidth: 0 }}>
          Live timeline
        </p>
        <span className={`pill ${statusTone(status)}`}>{status}</span>
        {/*
          Spend is a rollup over the step runs rather than a sum of the rows
          below, because a step accrues cost across turns that are not
          individually audited — adding up the timeline would under-report.
        */}
        <span className="pill">${(spendCents / 100).toFixed(2)}</span>
        <span className="pill" title={TRANSPORT_TIP[transport]}>
          {TRANSPORT_LABEL[transport]}
        </span>
      </div>

      {error && (
        <div className="banner danger">
          <h3>Lost the timeline</h3>
          <p style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      {events.length === 0 ? (
        <div className="empty">
          {transport === 'connecting'
            ? 'Waiting for the first event…'
            : 'Nothing has been recorded for this run yet.'}
        </div>
      ) : (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {events.map((event) => (
            <Entry key={event.id} event={event} />
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * The run's steps, handed in rather than fetched — for a pane that already
 * holds them.
 *
 * The full timeline above opens a stream per `runId`, and the work board's
 * drawer is the wrong place for one: a reader flicking between six cards would
 * open six streams and tear each down a second later, and every one of them
 * would spend its first second saying "connecting" over the facts the drawer
 * has already fetched with `api.getRun`. So this takes the steps and the run's
 * status as props and draws nothing it was not given. No audit rows either —
 * the refusals and the narrowings are the detail screen's argument, and the
 * question a drawer answers is the shorter one: which step is it on, what
 * failed, and where do I go to talk to it.
 *
 * The same status tones as the table on the run screen, so a step reads the
 * same colour here as it will there after "Open chat".
 */
export function CompactRunTimeline({
  steps,
  status,
  onOpenStep,
}: {
  steps: readonly StepRunSummary[];
  status: string;
  /** Offered on every row; a drawer without a way to the conversation is the link it replaced. */
  onOpenStep(step: StepRunSummary): void;
}) {
  const spendCents = steps.reduce((total, step) => total + step.spendCents, 0);

  return (
    <div className="stack drawer-steps">
      <div className="row wrap">
        <p className="section-title" style={{ margin: 0, flex: 1, minWidth: 0 }}>
          Steps
        </p>
        <span className={`pill ${statusTone(status)}`}>{status.replace(/_/g, ' ')}</span>
        <span className="pill">${(spendCents / 100).toFixed(2)}</span>
      </div>

      {steps.length === 0 ? (
        <p className="hint" style={{ margin: 0 }}>
          This run has not started any steps yet.
        </p>
      ) : (
        <ol>
          {steps.map((step) => (
            <li key={step.conversationId}>
              <span className={`run-dot ${statusTone(step.status)}`} aria-hidden="true" />
              <span className="drawer-step-body">
                <span className="drawer-step-head">
                  <strong>{step.stepId}</strong>
                  <span className="hint">{step.status.replace(/_/g, ' ')}</span>
                  <span className="hint">${(step.spendCents / 100).toFixed(2)}</span>
                </span>
                {/* On the row, as on the run screen: a bare "failed" sends a reader hunting through a transcript. */}
                {step.error && <span className="drawer-step-error">{step.error}</span>}
              </span>
              <button className="ghost" onClick={() => onOpenStep(step)}>
                Open chat
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * One row, at one of two weights.
 *
 * A refusal or a narrowing gets a banner with a headline sentence; everything
 * else gets a single quiet line. The alternative — a uniform table with a
 * coloured chip per row, which is what the audit log is — is the right shape
 * for searching afterwards and the wrong one for watching, because scanning a
 * live table means reading every row to find the one that matters.
 */
function Entry({ event }: { event: AuditEvent }) {
  const facts = policyFacts(event);
  const tone = toneFor(event, facts);
  const shape = SHAPES[tone];
  const when = new Date(event.at).toLocaleTimeString();

  const rail = {
    borderLeft: `2px solid ${shape.rail}`,
    paddingLeft: '0.875rem',
    paddingBottom: '0.6rem',
  };

  if (!shape.loud) {
    return (
      /*
        Two lines, not one. The summary is a sentence and the rest is metadata;
        sharing a flex row squeezed the sentence into a ~25-character column and
        turned every event into a ragged paragraph. Metadata scans across, prose
        reads down.
      */
      <li style={rail}>
        <div className="tl-event">
          <div className="row wrap tl-meta">
            <span className="hint" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {when}
            </span>
            <span className={`pill ${shape.pill}`}>{event.type}</span>
            {event.stepId && <code style={{ fontSize: '0.75rem' }}>{event.stepId}</code>}
            <Who event={event} />
          </div>
          {event.summary && <p className="tl-summary">{event.summary}</p>}
        </div>
      </li>
    );
  }

  return (
    <li style={rail}>
      <div className={`banner ${shape.banner}`} style={{ marginBottom: 0 }}>
        <div className="row wrap">
          <span className="hint" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {when}
          </span>
          <span className={`pill ${shape.pill}`}>{shape.label}</span>
          {event.stepId && <code style={{ fontSize: '0.75rem' }}>{event.stepId}</code>}
          <Who event={event} />
        </div>

        <h3 style={{ margin: '0.4rem 0 0' }}>{headline(event, facts)}</h3>

        {facts && facts.actions.length > 0 && (
          // Each action verbatim from the policy engine rather than summarised
          // as "3 changes", because "dropped column ssn" is the fact somebody
          // needs to see and a count of it is not.
          <div className="row wrap" style={{ marginTop: '0.4rem' }}>
            {facts.actions.map((action, index) => (
              <span key={index} className="pill warn">
                {action}
              </span>
            ))}
          </div>
        )}

        {facts && facts.rowsIn !== null && facts.rowsOut !== null && facts.rowsIn !== facts.rowsOut && (
          <p className="hint" style={{ margin: '0.4rem 0 0' }}>
            The step was told the result was cut from {facts.rowsIn} rows to {facts.rowsOut}, so it
            cannot report a partial answer as complete.
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * The identity column from the audit log, in a row instead of a cell.
 *
 * Same badge and same wording deliberately: a reader who learned what the
 * dashed violet outline means on the Connections screen should not have to
 * learn it again here.
 */
function Who({ event }: { event: AuditEvent }) {
  if (event.identity) {
    const kind = identityKindFor(event.identity.kind);
    const via = event.identity.connectionId ? ` via ${event.identity.connectionId}` : '';
    return (
      <IdentityBadge
        kind={kind}
        label={event.identity.display ?? event.identity.id}
        tip={
          kind === 'person'
            ? `A person's own credential${via}. This carried their name and their permissions.`
            : `A shared workspace credential${via}. No individual's access was used, and no individual is accountable for it.`
        }
      />
    );
  }

  if (event.actorUserId) {
    return (
      <IdentityBadge
        kind="person"
        label={event.actorEmail ?? event.actorUserId}
        tip={
          event.actorEmail
            ? `Reef user ${event.actorUserId}.`
            : 'No account with this id any more.'
        }
      />
    );
  }

  return <IdentityBadge kind="bot" label="system" tip="Reef's own control plane, not a step." />;
}

/*
 * The wire shapes are declared here rather than in `api.ts` because they belong
 * to the stream, not to the shared client — nothing else calls these endpoints.
 */

interface TimelineRun {
  id: string;
  workflowId: string;
  revision: number;
  status: string;
  triggeredBy: string | null;
}

interface TimelineStep {
  stepId: string;
  conversationId: string;
  status: string;
  spendCents: number;
  error: string | null;
}

/** Every frame the stream sends is a subset of the same object. */
interface TimelinePayload {
  run?: TimelineRun;
  steps?: TimelineStep[];
  events?: AuditEvent[];
  cursor?: string | null;
}

/**
 * Fold new events into what is on screen.
 *
 * Keyed on id and re-sorted rather than appended, because the same event
 * arrives twice by design: the server's cursor filter is inclusive on the
 * timestamp, and a reconnect replays a whole snapshot over rows already here.
 * Sorting by id after the timestamp keeps two events written in the same
 * millisecond in a stable order, so the list does not reshuffle under a reader.
 */
function merge(previous: readonly AuditEvent[], incoming: readonly AuditEvent[]): AuditEvent[] {
  const byId = new Map<string, AuditEvent>();
  for (const event of previous) byId.set(event.id, event);
  for (const event of incoming) byId.set(event.id, event);
  return [...byId.values()].sort((left, right) =>
    left.at === right.at ? left.id.localeCompare(right.id) : left.at.localeCompare(right.at),
  );
}

interface PolicyFacts {
  allowed: boolean;
  tool: string | null;
  actions: string[];
  blockedReason: string | null;
  rowsIn: number | null;
  rowsOut: number | null;
}

/**
 * What the policy engine did to one call, from whichever event carries it.
 *
 * `tool.call` and `policy.decision` record the same decision under different
 * key names — one is the call, the other is the ruling — and the timeline needs
 * the same answer from both, so the reading happens once here rather than being
 * duplicated in two branches of the renderer.
 */
function policyFacts(event: AuditEvent): PolicyFacts | null {
  const detail = event.detail;
  if (!detail) return null;
  if (event.type !== 'policy.decision' && event.type !== 'tool.call') return null;

  const actions = detail.actions ?? detail.policyActions;

  return {
    allowed: detail.allowed !== false,
    tool: typeof detail.tool === 'string' ? detail.tool : null,
    actions: Array.isArray(actions)
      ? actions.filter((entry): entry is string => typeof entry === 'string')
      : [],
    blockedReason: typeof detail.blockedReason === 'string' ? detail.blockedReason : null,
    rowsIn: typeof detail.rowsIn === 'number' ? detail.rowsIn : null,
    rowsOut: typeof detail.rowsOut === 'number' ? detail.rowsOut : null,
  };
}

type Tone = 'refused' | 'narrowed' | 'staged' | 'escalated' | 'call' | 'done' | 'quiet';

/**
 * Which of these facts is worth interrupting a reader for.
 *
 * An allowed call that the policy did not change is the ordinary case and stays
 * quiet even though it is a `policy.decision` — promoting it would mean every
 * tool call renders as a banner, and the refusals would be no easier to find
 * than they are in the audit table.
 */
function toneFor(event: AuditEvent, facts: PolicyFacts | null): Tone {
  if (event.type === 'connection.denied' || event.type === 'approval.rejected') return 'refused';
  if (event.type === 'approval.requested') return 'staged';
  if (event.type === 'runtime.escalated') return 'escalated';

  if (facts && !facts.allowed) return 'refused';
  if (event.type === 'policy.decision') {
    return facts && facts.actions.length > 0 ? 'narrowed' : 'quiet';
  }
  if (event.type === 'tool.call') return 'call';
  if (event.type === 'step.finished' || event.type === 'run.finished') return 'done';
  return 'quiet';
}

const SHAPES: Record<
  Tone,
  { loud: boolean; pill: string; banner: string; rail: string; label: string }
> = {
  refused: { loud: true, pill: 'danger', banner: 'danger', rail: 'var(--danger)', label: 'refused' },
  narrowed: { loud: true, pill: 'warn', banner: 'warn', rail: 'var(--warn)', label: 'narrowed' },
  staged: { loud: true, pill: 'warn', banner: 'warn', rail: 'var(--warn)', label: 'staged' },
  escalated: {
    loud: true,
    pill: 'accent',
    banner: 'warn',
    rail: 'var(--accent)',
    label: 'escalated',
  },
  call: { loud: false, pill: 'accent', banner: '', rail: 'var(--border)', label: 'tool call' },
  done: { loud: false, pill: 'ok', banner: '', rail: 'var(--border)', label: 'finished' },
  quiet: { loud: false, pill: '', banner: '', rail: 'var(--border)', label: '' },
};

/**
 * The sentence a prominent row leads with.
 *
 * Written as a claim about what reef did rather than as the event name, because
 * "policy.decision" tells a reader nothing and "db.query was truncated to the
 * policy ceiling" tells them the entire point.
 */
function headline(event: AuditEvent, facts: PolicyFacts | null): string {
  const tool = facts?.tool ?? 'The call';

  switch (event.type) {
    case 'connection.denied':
      return event.summary ?? 'A connection this step asked for was refused.';
    case 'approval.rejected':
      return event.summary ?? 'A staged write was rejected.';
    case 'approval.requested':
      return event.summary ?? 'A write was staged for approval instead of being performed.';
    case 'runtime.escalated':
      return event.summary ?? 'The step moved from an isolate to a container and replayed.';
    default:
      break;
  }

  if (facts && !facts.allowed) {
    return facts.blockedReason
      ? `${tool} was blocked: ${facts.blockedReason}`
      : `${tool} was blocked by policy.`;
  }

  return `${tool} ran, and policy changed the result before the model saw it.`;
}

function statusTone(status: string): string {
  if (status === 'completed') return 'ok';
  if (status === 'failed' || status === 'cancelled') return 'danger';
  if (status === 'running') return 'accent';
  return 'warn';
}

type Transport = 'connecting' | 'live' | 'polling' | 'closed';

const TRANSPORT_LABEL: Record<Transport, string> = {
  connecting: 'connecting',
  live: 'live',
  polling: 'polling',
  closed: 'closed',
};

/** Said out loud, because a reader watching a run needs to know it is watching. */
const TRANSPORT_TIP: Record<Transport, string> = {
  connecting: 'Opening the event stream.',
  live: 'Streaming from the audit log as it is written.',
  polling: 'The event stream did not come through, so this is refreshing on a timer instead.',
  closed: 'This run is finished; nothing further will arrive.',
};

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);
const POLL_INTERVAL_MS = 3_000;

/**
 * How long a stream may say nothing before it is treated as broken. Longer than
 * the server's heartbeat, so an idle run is never mistaken for a dead pipe.
 */
const STREAM_WATCHDOG_MS = 20_000;
